/**
 * FormatPicker - Modal/Drawer for choosing alternative formats
 * Shows all formats with minimal metadata (no scoring)
 * Scores format on-demand when user selects
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { EMZI_COLORS } from '../../../styles/emziCI';
import { BACKEND_URL } from '../../../utils/backendUrl';

const FormatPicker = ({ isOpen, onClose, onSelectFormat, token, sessionId }) => {
  const [formats, setFormats] = useState([]);
  const [filteredFormats, setFilteredFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states (persist when reopening)
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [selectedDuration, setSelectedDuration] = useState('All');
  const [selectedProofMode, setSelectedProofMode] = useState('All');
  
  // Refs for accessibility
  const modalRef = useRef(null);
  const backButtonRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Load formats when opened
  useEffect(() => {
    if (isOpen) {
      loadFormats();
      
      // Focus on Back button when opened (accessibility)
      setTimeout(() => {
        if (backButtonRef.current) {
          backButtonRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);
  
  // ESC key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedPlatform, selectedDuration, selectedProofMode, formats]);

  const loadFormats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/v2/content/format/list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load formats');
      }
      
      const data = await response.json();
      setFormats(data.formats || []);
      setFilteredFormats(data.formats || []);
      setLoading(false);
    } catch (err) {
      console.error('[FormatPicker] Error loading formats:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...formats];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(f => 
        f.format_name.toLowerCase().includes(query) ||
        f.summary.toLowerCase().includes(query)
      );
    }
    
    // Platform filter
    if (selectedPlatform !== 'All') {
      filtered = filtered.filter(f => 
        f.supported_platforms.includes(selectedPlatform)
      );
    }
    
    // Duration filter
    if (selectedDuration !== 'All') {
      filtered = filtered.filter(f => {
        const duration = f.duration_ideal;
        if (selectedDuration === 'Short' && duration.includes('15-') || duration.includes('30-')) return true;
        if (selectedDuration === 'Medium' && duration.includes('60-')) return true;
        if (selectedDuration === 'Long' && duration.includes('120-') || duration.includes('180-')) return true;
        return false;
      });
    }
    
    // Proof mode filter
    if (selectedProofMode !== 'All') {
      filtered = filtered.filter(f => f.proof_mode === selectedProofMode);
    }
    
    setFilteredFormats(filtered);
  };

  const handleSelectFormat = (format) => {
    console.log('[FormatPicker] Selected format:', format.format_id, format.format_name);
    onSelectFormat(format.format_id);
    handleClose();
  };
  
  const handleClose = () => {
    console.log('[FormatPicker] Closing panel');
    // Note: We preserve filter states for next open
    onClose();
    
    // Return focus to Step 8 anchor (if exists)
    setTimeout(() => {
      const top5Element = document.getElementById('top5');
      if (top5Element) {
        top5Element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  if (!isOpen) return null;
  
  // Detect mobile
  const isMobile = window.innerWidth <= 768;

  // Modal content
  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        zIndex: 999999,  // Extremely high z-index
        padding: 0
      }}
      onClick={(e) => {
        // Close when clicking backdrop (desktop only)
        if (!isMobile && e.target === e.currentTarget) {
          handleClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="choose-format-title"
    >
      <div 
        ref={modalRef}
        style={{
          background: '#FFFFFF',
          borderRadius: isMobile ? '0' : '16px',
          maxWidth: isMobile ? '100%' : '840px',
          width: '100%',
          height: isMobile ? '100vh' : '90vh',
          maxHeight: isMobile ? '100vh' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          right: isMobile ? 0 : 'auto',
          bottom: isMobile ? 0 : 'auto'
        }}
      >
        {/* Header Bar - Sticky with Back and Close */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '12px 16px' : '16px 20px',
          paddingTop: isMobile ? 'calc(12px + env(safe-area-inset-top, 0px))' : '16px',
          borderBottom: '2px solid #EEF2F7',
          background: '#FFFFFF',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          flexShrink: 0,
          minHeight: isMobile ? '56px' : '64px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {/* Left: Back Button */}
          <button
            ref={backButtonRef}
            onClick={handleClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '4px' : '8px',
              background: 'none',
              border: 'none',
              fontSize: isMobile ? '14px' : '15px',
              fontWeight: '600',
              color: EMZI_COLORS.primary,
              cursor: 'pointer',
              padding: isMobile ? '6px 8px' : '8px 12px',
              borderRadius: '8px',
              transition: 'background 0.2s',
              flexShrink: 0
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
            aria-label="Back to Step 8"
          >
            <span style={{ fontSize: isMobile ? '16px' : '18px' }}>←</span>
            <span>Back</span>
          </button>
          
          {/* Center: Title */}
          <h2 
            id="choose-format-title"
            style={{
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: '700',
              color: '#111827',
              margin: 0,
              textAlign: 'center',
              flex: 1,
              whiteSpace: isMobile ? 'nowrap' : 'normal',
              overflow: isMobile ? 'hidden' : 'visible',
              textOverflow: isMobile ? 'ellipsis' : 'clip'
            }}
          >
            {isMobile ? 'Choose Format' : 'Choose Another Format'}
          </h2>
          
          {/* Right: Close Button */}
          <button
            ref={closeButtonRef}
            onClick={handleClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              fontSize: isMobile ? '24px' : '28px',
              cursor: 'pointer',
              color: '#9CA3AF',
              padding: isMobile ? '6px 8px' : '8px 12px',
              borderRadius: '8px',
              transition: 'all 0.2s',
              lineHeight: '1',
              width: isMobile ? '36px' : '40px',
              height: isMobile ? '36px' : '40px',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#FEE2E2';
              e.currentTarget.style.color = '#DC2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = '#9CA3AF';
            }}
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>
        
        {/* Format Count Badge */}
        <div style={{
          padding: isMobile ? '8px 16px' : '12px 20px',
          background: '#F9FAFB',
          borderBottom: '1px solid #E5E7EB',
          flexShrink: 0
        }}>
          <p style={{ 
            fontSize: isMobile ? '12px' : '13px', 
            color: '#6B7280',
            margin: 0,
            textAlign: 'center'
          }}>
            📋 <strong>{filteredFormats.length}</strong> formats {searchQuery || selectedPlatform !== 'All' || selectedDuration !== 'All' || selectedProofMode !== 'All' ? `(filtered)` : 'available'}
          </p>
        </div>

        {/* Search & Filters - Sticky */}
        <div style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid #E5E7EB',
          background: '#FFFFFF',
          position: 'sticky',
          top: 0,
          zIndex: 3,
          flexShrink: 0
        }}>
          {/* Search */}
          <input
            type="text"
            placeholder="Search formats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              marginBottom: '12px'
            }}
          />
          
          {/* Filter Chips */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {/* Platform Filter */}
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '12px',
                background: selectedPlatform !== 'All' ? EMZI_COLORS.primary : '#FFFFFF',
                color: selectedPlatform !== 'All' ? '#FFFFFF' : '#374151',
                cursor: 'pointer'
              }}
            >
              <option value="All">All Platforms</option>
              <option value="TikTok">TikTok</option>
              <option value="Instagram">Instagram</option>
              <option value="Facebook">Facebook</option>
              <option value="YouTube">YouTube</option>
              <option value="Landing Page">Landing Page</option>
            </select>

            {/* Duration Filter */}
            <select
              value={selectedDuration}
              onChange={(e) => setSelectedDuration(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '12px',
                background: selectedDuration !== 'All' ? EMZI_COLORS.primary : '#FFFFFF',
                color: selectedDuration !== 'All' ? '#FFFFFF' : '#374151',
                cursor: 'pointer'
              }}
            >
              <option value="All">All Durations</option>
              <option value="Short">Short (15-30s)</option>
              <option value="Medium">Medium (60-90s)</option>
              <option value="Long">Long (120-180s)</option>
            </select>

            {/* Proof Mode Filter */}
            <select
              value={selectedProofMode}
              onChange={(e) => setSelectedProofMode(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '12px',
                background: selectedProofMode !== 'All' ? EMZI_COLORS.primary : '#FFFFFF',
                color: selectedProofMode !== 'All' ? '#FFFFFF' : '#374151',
                cursor: 'pointer'
              }}
            >
              <option value="All">All Modes</option>
              <option value="Core/Proof">Core/Proof</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Emotion">Emotion</option>
            </select>

            {/* Clear Filters */}
            {(searchQuery || selectedPlatform !== 'All' || selectedDuration !== 'All' || selectedProofMode !== 'All') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedPlatform('All');
                  setSelectedDuration('All');
                  setSelectedProofMode('All');
                }}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  background: '#FEE2E2',
                  color: '#991B1B',
                  cursor: 'pointer'
                }}
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Format List - Scrollable with proper spacing */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',  // Smooth iOS scrolling
          padding: '16px 16px',  // Consistent gutters
          paddingBottom: `calc(16px + env(safe-area-inset-bottom, 0px))`,  // Safe-area for iOS
          maxWidth: '720px',
          margin: '0 auto',
          width: '100%'
        }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
              Loading formats...
            </div>
          )}

          {error && (
            <div style={{
              padding: '20px',
              background: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: '8px',
              color: '#991B1B',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && filteredFormats.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
              No formats match your filters
            </div>
          )}

          {/* Format Cards with proper vertical rhythm */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '16px'  // Consistent 16px gap between cards
          }}>
            {!loading && !error && filteredFormats.map((format) => (
              <div
                key={format.format_id}
                onClick={() => handleSelectFormat(format)}
                style={{
                  padding: '14px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: '#FFFFFF',
                  overflow: 'hidden'  // Prevent child elements from bleeding
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = EMZI_COLORS.primary;
                  e.currentTarget.style.background = '#F0F9FF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.background = '#FFFFFF';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <span style={{ fontSize: '24px', flexShrink: 0 }}>{format.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>  {/* minWidth: 0 prevents flex overflow */}
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                      {format.format_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px', lineHeight: '1.4' }}>
                      {format.summary}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '0' }}>  {/* No negative margins */}
                      {/* Duration Badge */}
                      <span style={{
                        fontSize: '10px',
                        padding: '3px 8px',
                        background: '#DBEAFE',
                        color: '#1E40AF',
                        borderRadius: '4px',
                        fontWeight: 500,
                        whiteSpace: 'nowrap'
                      }}>
                        ⏱️ {format.duration_ideal}
                      </span>
                      
                      {/* Proof Mode Badge */}
                      <span style={{
                        fontSize: '10px',
                        padding: '3px 8px',
                        background: '#E0E7FF',
                        color: '#4338CA',
                        borderRadius: '4px',
                        fontWeight: 500,
                        whiteSpace: 'nowrap'
                      }}>
                        {format.proof_mode}
                      </span>
                      
                      {/* Platform Badges */}
                      {format.supported_platforms.slice(0, 3).map((platform, idx) => (
                        <span
                          key={`item-${idx}`}
                          style={{
                            fontSize: '10px',
                            padding: '3px 8px',
                            background: '#F3F4F6',
                            color: '#374151',
                            borderRadius: '4px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {platform}
                        </span>
                      ))}
                      {format.supported_platforms.length > 3 && (
                        <span style={{
                          fontSize: '10px',
                          padding: '3px 8px',
                          color: '#6B7280',
                          fontWeight: 500
                        }}>
                          +{format.supported_platforms.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  
  // Use React Portal to render modal at document.body level (escape stacking context)
  return ReactDOM.createPortal(modalContent, document.body);
};

export default FormatPicker;
